import { Box, Skeleton, Typography } from '@mui/material';
import { theme } from '@side/pantry';
import { useCallback, useEffect, useRef, useState } from 'react';
import { formatPrice } from '@/common/utils/money';
import { GlossaryTooltip } from '../components/GlossaryTooltip';

export const BORDER_ROW = theme.palette.grey[300];
const HIGHLIGHT_BG = 'rgba(237, 242, 251, 0.65)';
const HEADER_BG = theme.palette.background.default;

/** Pin label column on small viewports while horizontally scrolling values */
const STICKY_FIRST_COL_SHADOW = '4px 0 12px -4px rgba(39, 43, 54, 0.12)';

function stickyFirstColSx(showEdgeShadow: boolean, zIndex: 1 | 2) {
  return {
    position: { xs: 'sticky', md: 'relative' },
    left: { xs: 0 },
    zIndex: { xs: zIndex, md: 'auto' },
    boxShadow: {
      xs: showEdgeShadow ? STICKY_FIRST_COL_SHADOW : 'none',
      md: 'none',
    },
  };
}

export function formatSignedCurrency(value: number, appendCents: boolean) {
  return `${value < 0 ? '-' : ''}${formatPrice(Math.abs(value), true, appendCents)}`;
}

const baseCellSx = {
  borderBottom: `1px solid ${BORDER_ROW}`,
  p: 2,
  height: { xs: 40, md: 46 },
  display: 'flex',
  alignItems: 'center',
  boxSizing: 'border-box' as const,
};

export function labelCellSx(extra?: object) {
  return {
    ...baseCellSx,
    backgroundColor: 'background.paper',
    minWidth: 0,
    ...extra,
  };
}

export function valueCellSx(extra?: object) {
  return {
    ...baseCellSx,
    justifyContent: 'flex-end',
    textAlign: 'center',
    ...extra,
  };
}

export const highlightedCellSx = labelCellSx({
  borderBottomWidth: '1px',
  borderBottomColor: BORDER_ROW,
  backgroundColor: HIGHLIGHT_BG,
  height: { xs: 46, md: 50 },
  py: '12px',
});

export function PayoutValueSkeleton() {
  return (
    <Skeleton
      variant="rectangular"
      sx={{
        flexShrink: 0,
        borderRadius: '4px',
        width: 81,
      }}
    />
  );
}

export const getGridTemplateColumns = (columns: number) => {
  if (columns === 1) {
    return {
      xs: 'minmax(0, 270px) minmax(0, 1fr)',
    };
  }

  return {
    xs: `minmax(0, 170px) repeat(${columns}, minmax(96px, 1fr))`,
    md: `minmax(140px, 300px) repeat(${columns}, minmax(96px, 1fr))`,
  };
};

const headerCellBaseSx = {
  backgroundColor: HEADER_BG,
  px: 1,
  py: 0.75,
  display: 'flex',
  alignItems: 'center',
  minHeight: 38,
  minWidth: 0,
  boxSizing: 'border-box' as const,
};

type RowDef = {
  label: string;
  glossaryCaption?: string;
  glossaryLabel?: string;
  agent: number | null;
  brand: number | null | undefined;
  team: number | null | undefined;
  highlight?: boolean;
  appendCents?: boolean;
};

export interface BreakdownGridProps {
  isLoading: boolean;
  showBrand: boolean;
  showTeam: boolean;
  agentGrossShare: number | null;
  agentFeesReceived: number | null;
  agentFeesPaid: number | null;
  agentNetShare: number | null;
  brandGrossShare: number | null;
  brandFeesReceived: number | null;
  brandFeesPaid: number | null;
  brandNetShare: number | null;
  teamGrossShare: number | null;
  teamFeesReceived: number | null;
  teamFeesPaid: number | null;
  teamNetShare: number | null;
}

export function BreakdownGrid({
  isLoading,
  showBrand,
  showTeam,
  agentGrossShare,
  agentFeesReceived,
  agentFeesPaid,
  agentNetShare,
  brandGrossShare,
  brandFeesReceived,
  brandFeesPaid,
  brandNetShare,
  teamGrossShare,
  teamFeesReceived,
  teamFeesPaid,
  teamNetShare,
}: BreakdownGridProps) {
  const columns = 1 + (showBrand ? 1 : 0) + (showTeam ? 1 : 0);

  const scrollRef = useRef<HTMLDivElement>(null);
  const [showFirstColShadow, setShowFirstColShadow] = useState(false);

  const syncFirstColShadow = useCallback(() => {
    const root = scrollRef.current;
    if (root) {
      setShowFirstColShadow(root.scrollLeft > 0);
    }
  }, []);

  useEffect(() => {
    syncFirstColShadow();
  }, [syncFirstColShadow, columns, showBrand, showTeam, isLoading]);

  useEffect(() => {
    const root = scrollRef.current;
    if (!root) {
      return;
    }
    const ro = new ResizeObserver(() => {
      syncFirstColShadow();
    });
    ro.observe(root);
    return () => ro.disconnect();
  }, [syncFirstColShadow]);

  const gridTemplateColumns = getGridTemplateColumns(columns);

  const showHeader = showBrand || showTeam;
  const headerRow = showHeader ? (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns,
        borderBottom: `1px solid ${BORDER_ROW}`,
        borderTop: `1px solid ${BORDER_ROW}`,
        minHeight: 38,
      }}
    >
      <Box
        sx={{
          ...headerCellBaseSx,
          ...stickyFirstColSx(showFirstColShadow, 2),
        }}
      />
      {showBrand ? (
        <Box sx={{ ...headerCellBaseSx, justifyContent: 'center' }}>
          <Typography variant="subtitle2">Brand</Typography>
        </Box>
      ) : null}
      {showTeam ? (
        <Box sx={{ ...headerCellBaseSx, justifyContent: 'center' }}>
          <Typography variant="subtitle2">Team</Typography>
        </Box>
      ) : null}
      <Box sx={{ ...headerCellBaseSx, justifyContent: 'center' }}>
        <Typography variant="subtitle2">Agent</Typography>
      </Box>
    </Box>
  ) : null;

  const rows: RowDef[] = [
    {
      label: 'Gross Commission',
      glossaryLabel: 'Gross Commission',
      glossaryCaption: 'The portion of commission before fees are deducted',
      agent: agentGrossShare,
      brand: showBrand ? brandGrossShare : undefined,
      team: showTeam ? teamGrossShare : undefined,
    },
    {
      label: 'Fees Received',
      agent: agentFeesReceived,
      brand: showBrand ? brandFeesReceived : undefined,
      team: showTeam ? teamFeesReceived : undefined,
    },
    {
      label: 'Fees Paid',
      agent: agentFeesPaid,
      brand: showBrand ? brandFeesPaid : undefined,
      team: showTeam ? teamFeesPaid : undefined,
    },
    {
      label: 'Net Commission',
      glossaryLabel: 'Net Commission',
      glossaryCaption:
        'The final take-home commission after all splits and fees',
      agent: agentNetShare,
      brand: showBrand ? brandNetShare : undefined,
      team: showTeam ? teamNetShare : undefined,
      highlight: true,
    },
  ];

  return (
    <Box
      ref={scrollRef}
      onScroll={syncFirstColShadow}
      sx={{
        width: '100%',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        // Always show horizontal scrollbar on mobile so users know content is scrollable
        '&::-webkit-scrollbar': {
          height: 6,
          display: { xs: 'block', md: 'none' },
        },
        '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: theme.palette.grey[400],
          borderRadius: 3,
        },
      }}
    >
      <Box sx={{ minWidth: '100%', width: 'max-content' }}>
        {headerRow}
        {rows.map((row) => {
          const labelEl =
            row.glossaryLabel != null ? (
              <GlossaryTooltip
                label={row.glossaryLabel}
                caption={row.glossaryCaption}
              >
                <Typography
                  variant={row.highlight ? 'h5' : 'body2'}
                  component="span"
                >
                  {row.label}
                </Typography>
              </GlossaryTooltip>
            ) : (
              <Typography
                variant={row.highlight ? 'h5' : 'body2'}
                sx={{
                  color: row.highlight ? 'accent.spaceCadet' : 'text.primary',
                }}
              >
                {row.label}
              </Typography>
            );

          const valueContent = (
            v: number | null | undefined,
            highlight: boolean = false,
          ) => {
            if (isLoading) return <PayoutValueSkeleton />;
            if (typeof v !== 'number') {
              return (
                <Typography
                  variant={row.highlight ? 'h5' : 'body2'}
                  sx={{ color: 'text.disabled' }}
                >
                  –
                </Typography>
              );
            }
            return (
              <Typography
                variant={row.highlight ? 'h5' : 'body2'}
                sx={{
                  color: highlight ? 'accent.spaceCadet' : 'text.secondary',
                }}
              >
                {formatSignedCurrency(v, row.appendCents ?? false)}
              </Typography>
            );
          };

          return (
            <Box key={row.label} sx={{ display: 'grid', gridTemplateColumns }}>
              <Box
                sx={
                  row.highlight
                    ? {
                        ...highlightedCellSx,
                        ...stickyFirstColSx(showFirstColShadow, 1),
                        borderBottom: 'none',
                      }
                    : {
                        ...labelCellSx(),
                        ...stickyFirstColSx(showFirstColShadow, 1),
                      }
                }
              >
                {labelEl}
              </Box>
              {showBrand ? (
                <Box
                  sx={
                    row.highlight
                      ? {
                          ...highlightedCellSx,
                          justifyContent: 'center',
                          borderBottom: 'none',
                        }
                      : valueCellSx({ justifyContent: 'center' })
                  }
                >
                  {valueContent(row.brand ?? null, row.highlight)}
                </Box>
              ) : null}
              {showTeam ? (
                <Box
                  sx={
                    row.highlight
                      ? {
                          ...highlightedCellSx,
                          justifyContent: 'center',
                          borderBottom: 'none',
                        }
                      : valueCellSx({ justifyContent: 'center' })
                  }
                >
                  {valueContent(row.team ?? null, row.highlight)}
                </Box>
              ) : null}
              <Box
                sx={
                  row.highlight
                    ? {
                        ...highlightedCellSx,
                        justifyContent: 'center',
                        borderBottom: 'none',
                      }
                    : valueCellSx({ justifyContent: 'center' })
                }
              >
                {valueContent(row.agent, row.highlight)}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
